import {
  Badge,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerProps,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { DocumentTextIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';
import { useTranslations } from 'use-intl';

import {
  BasicAssetInfosFragment,
  CompanyDocumentType,
  ContractType,
  DocumentStatus,
  FundraisingDocumentType,
  GetAssetDocumentsQuery,
  Identity,
  IdentityType,
  MovementInfosFragment,
  MovementType,
  SecurityTokenTransferModel,
  StockAssetInfosFragment,
  useGetAssetDocumentsLazyQuery,
  useGetBasicAssetLazyQuery,
  useGetDocumentPdfUrlLazyQuery,
  useGetMovementsByAssetAndIdentityLazyQuery,
  useGetStockAssetLazyQuery,
  useGetWalletQuery,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import {
  AssetBadge,
  CompanyIdentityAvatar,
  ErrorQueryCard,
  IdentityAvatar,
  IdentityCard,
  IdentityCardDescription,
  IdentityCardTitle,
} from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

import { MergedStockAssetByType } from '../types';

type Props = {
  mergedBalances: MergedStockAssetByType | null;
  identityId: Identity['id'] | null;
} & Omit<DrawerProps, 'children'>;

const DISPLAYED_DOCUMENTS = [
  CompanyDocumentType.STATUTS,
  FundraisingDocumentType.SUBSCRIPTION,
  FundraisingDocumentType.IR_PME_CERTIFICATE,
  FundraisingDocumentType.PEA_CERTIFICATE,
  FundraisingDocumentType.PEA_PME_CERTIFICATE,
  FundraisingDocumentType.OWNERSHIP_CERTIFICATE,
  FundraisingDocumentType.SHAREHOLDER_AGREEMENT,
  FundraisingDocumentType.CUSTOM_FILE,
];

/**
 * StockAssetSidePanel.
 * Display stock asset details. It is also used for the old basic token contract.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function StockAssetSidePanel({ mergedBalances, identityId, ...props }: Props): JSX.Element {
  const i18nBadge = useTranslations('Badge');
  const i18nStockAssetSidePanel = useTranslations('StockAssetSidePanel');
  const i18nDocumentValue = useTranslations('DocumentValues');

  const [getBasicAssetQuery] = useGetBasicAssetLazyQuery();
  const [getStockAssetQuery] = useGetStockAssetLazyQuery();
  const [getMovementsByAssetAndIdentity] = useGetMovementsByAssetAndIdentityLazyQuery();
  const [getAssetDocument] = useGetAssetDocumentsLazyQuery();
  const [getDocumentPdfUrl] = useGetDocumentPdfUrlLazyQuery();

  const [movements, setMovements] = useState<MovementInfosFragment[] | null | undefined>(null);

  const [stockAssets, setStockAssets] = useState<
    StockAssetInfosFragment[] | BasicAssetInfosFragment[] | null
  >(null);
  const [stockAssetsDocuments, setStockAssetsDocuments] = useState<
    GetAssetDocumentsQuery['getAssetDocuments'][]
  >([]);

  const getWallet = useGetWalletQuery({
    variables: {
      identityId: identityId || '',
    },
  });
  const walletId = getWallet.data?.getWallet?.id;

  const { isOpen } = props;

  useEffect(() => {
    if (!isOpen) {
      setStockAssets(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!identityId || !mergedBalances || !mergedBalances.balances || !isOpen) return;

    const fetchMovements = async (): Promise<void> => {
      const movementPromises = mergedBalances.balances.map(async (balance) => {
        const { data } = await getMovementsByAssetAndIdentity({
          variables: {
            assetId: balance.asset.id,
            identityId,
          },
        });
        return data?.getMovementsByAssetAndIdentity || [];
      });

      try {
        const results = await Promise.all(movementPromises);
        setMovements(results.flat());
      } catch (error) {
        console.log('Error', error);
      }
    };

    const fetchAssets = async (): Promise<void> => {
      // StockAssetInfosFragment | BasicAssetInfosFragment | null
      const assetPromises = mergedBalances.balances.map(async (balance) => {
        if (balance.asset.contractType === ContractType.STOCK_TOKEN) {
          const { data } = await getStockAssetQuery({
            variables: {
              getAssetInput: {
                assetId: balance.asset.id,
                companyId: balance.asset.company.id,
              },
            },
          });
          return data?.getStockAsset || null;
        } else {
          const { data } = await getBasicAssetQuery({
            variables: {
              getAssetInput: {
                assetId: balance.asset.id,
                companyId: balance.asset.company.id,
              },
            },
          });
          return data?.getBasicAsset || null;
        }
      });

      try {
        const results = await Promise.all(assetPromises);
        const trimedResult = results.filter((n) => n);
        setStockAssets(
          trimedResult as StockAssetInfosFragment[] | BasicAssetInfosFragment[] | null,
        );
      } catch (error) {
        console.log('Error', error);
      }
    };

    const fetchAssetDocuments = async (): Promise<void> => {
      const assetDocumentsPromises = mergedBalances.balances.map(async (balance) => {
        const { data } = await getAssetDocument({
          variables: {
            assetId: balance.asset.id,
            identityId,
          },
        });
        return data?.getAssetDocuments;
      });

      const results = await Promise.all(assetDocumentsPromises);
      const trimedResult = results.filter((n) => n);
      setStockAssetsDocuments(trimedResult);
    };

    fetchMovements();
    fetchAssets();
    fetchAssetDocuments();
  }, [
    mergedBalances,
    identityId,
    isOpen,
    getMovementsByAssetAndIdentity,
    getStockAssetQuery,
    getBasicAssetQuery,
    getAssetDocument,
  ]);

  const { getBadge } = useBadge(
    null,
    [
      { value: DocumentStatus.SIGNED, color: 'green', label: i18nBadge('Signed') },
      { value: DocumentStatus.ONGOING, color: 'yellow', label: i18nBadge('InProgress') },
    ],
    {
      color: 'gray',
      label: i18nBadge('NonSigned'),
    },
  );

  if (!stockAssets || !movements || !mergedBalances) {
    return (
      <Drawer {...props}>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p="0">
            <Stack alignItems="center" h="full" justifyContent="center" py="4" spacing="4">
              <Spinner />
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  /* ** First movement, mint attribution ** */
  const transfers: SecurityTokenTransferModel[] = [];
  stockAssets.forEach((stockAsset) => {
    stockAsset.transfers?.forEach((transfer) => {
      transfers.push(transfer);
    });
  });
  const sortedTransfers = [...transfers].sort((a, b) => a.date - b.date);
  const firstTransfer = sortedTransfers[0];
  if (!firstTransfer) {
    return (
      <Drawer {...props}>
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerBody p="0">
            <Stack alignItems="center" h="full" justifyContent="center" py="4" spacing="4">
              <ErrorQueryCard height="full" m="4" />
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  const allDocs = stockAssetsDocuments
    .flat()
    .filter((value, index, self) => index === self.findIndex((doc) => doc?.id === value?.id));

  const signedDocs = allDocs.filter(
    (doc) =>
      doc &&
      doc.status === DocumentStatus.SIGNED &&
      DISPLAYED_DOCUMENTS.includes(doc.type as CompanyDocumentType | FundraisingDocumentType),
  );

  const onDocumentClick = (documentId: string): void => {
    getDocumentPdfUrl({
      variables: {
        documentId,
      },
      fetchPolicy: 'no-cache',
      onCompleted: ({ getDocumentPdfUrl: pdfUrl }) => {
        window.open(pdfUrl, '_blank');
      },
    });
  };

  const { signer, contact } = mergedBalances;
  const repIndividualIdentity = contact?.individualIdentity || signer.individualIdentity;

  return (
    <Drawer {...props}>
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerBody p="0">
          <Stack py="4" spacing="4">
            <Stack alignItems="center" px="4" spacing="2">
              <Stack alignItems="center" spacing="1">
                <CompanyIdentityAvatar boxSize="20" src={mergedBalances?.company.profilePicture} />
                <Text color="gray.800" fontWeight="800">
                  {mergedBalances?.company.name}
                </Text>
              </Stack>
              <Text color="gray.900" fontSize="xl" fontWeight="700">
                {formatNumberCurrency(mergedBalances?.investAmount || 0)}
              </Text>
            </Stack>
            <Divider />
            {movements.map((el, index) => {
              return (
                <Fragment key={index}>
                  <Stack fontSize="sm" px="4" spacing="4">
                    <HStack>
                      <Text color="gray.600" fontWeight="600">
                        {el.type === MovementType.BURN ||
                        (el.type === MovementType.TRANSFER && el.fromWallet === walletId)
                          ? i18nStockAssetSidePanel('TitreAssetCession', {
                              date: dayjs(el.createdAt).format('DD MMMM YYYY'),
                            })
                          : i18nStockAssetSidePanel('TitreAssetAcquisition', {
                              date: dayjs(el.createdAt).format('DD MMMM YYYY'),
                            })}
                      </Text>
                    </HStack>
                    <HStack fontSize="sm" justifyContent="space-between">
                      <Text color="gray.500" fontWeight="500">
                        {i18nStockAssetSidePanel('PurchasePrice')}
                      </Text>
                      <Text fontWeight="500">
                        {i18nStockAssetSidePanel('PricePerShare', {
                          price: el.pricePerUnit ? formatNumberCurrency(el.pricePerUnit) : '-',
                        })}
                      </Text>
                    </HStack>
                    <HStack fontSize="sm" justifyContent="space-between">
                      <Text color="gray.500" fontWeight="500">
                        {i18nStockAssetSidePanel('ShareCount')}
                      </Text>
                      <Stack alignItems="flex-end" flexShrink="0" spacing="0">
                        <AssetBadge type={el.asset.type} value={el.amount} />
                      </Stack>
                    </HStack>
                  </Stack>
                  <Divider />
                </Fragment>
              );
            })}
            {signedDocs.length && (
              <>
                <Stack px="4" spacing="4">
                  <Text color="gray.600" fontWeight="600">
                    {i18nStockAssetSidePanel('Document')}
                  </Text>
                  <Stack spacing="5">
                    {signedDocs.map((document) => {
                      if (!document) return null;

                      const { label, color } = getBadge(document.status);
                      return (
                        <HStack key={document.id} justifyContent="space-between">
                          <Button variant="link" onClick={(): void => onDocumentClick(document.id)}>
                            <HStack>
                              <Icon as={DocumentTextIcon} boxSize="24px" color="gray.500" />
                              <Text fontSize="sm">
                                {document.type === FundraisingDocumentType.CUSTOM_FILE
                                  ? document.title
                                  : i18nDocumentValue(
                                      `Document${document.type}` as keyof IntlMessages['DocumentValues'],
                                    )}
                              </Text>
                            </HStack>
                          </Button>
                          {document.type === FundraisingDocumentType.SUBSCRIPTION && (
                            <Badge colorScheme={color} fontSize="sm">
                              {label}
                            </Badge>
                          )}
                        </HStack>
                      );
                    })}
                  </Stack>
                </Stack>
                <Divider />
              </>
            )}
            <Stack px="4" spacing="4">
              <Text color="gray.600" fontWeight="600">
                {i18nStockAssetSidePanel(contact ? 'Contact' : 'President')}
              </Text>
              <IdentityCard bg="gray.50" p="3">
                <IdentityAvatar type={IdentityType.INDIVIDUAL} />
                <Stack spacing="0">
                  <IdentityCardTitle isChecked>
                    <Text>{repIndividualIdentity?.name}</Text>
                  </IdentityCardTitle>
                  <IdentityCardDescription>
                    <Text>{repIndividualIdentity?.email}</Text>
                  </IdentityCardDescription>
                </Stack>
              </IdentityCard>
            </Stack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export type StockAssetSidePanelProps = Props;
